import { defineStore } from 'pinia'
import { ref, watch, computed } from 'vue'
import { TaskLibraryApi } from '@/api/modules/task-library'
import {
  IAgent,
  IAgentCategory,
  IAgentsByCategory,
  IListAgentCategoryRequestQuery,
  IListAgentRequestQuery,
} from '@/api/modules/task-library/types'
import { useRouter } from 'vue-router'

export const useAgentsLibraryStore = defineStore('agentsLibraryStore', () => {
  const router = useRouter()
  const agentId = ref(router.currentRoute.value.query.agent_id?.toString())

  const agentCategoryItems = ref<IAgentCategory[]>([])
  const agentCategoryTotal = ref(0)
  const countAgents = ref<number>(0)
  const agentsItems = ref<IAgent[]>([])
  const agentsByCategory = ref<IAgentsByCategory>({})

  const getCategoryItems = async (param?: Partial<IListAgentCategoryRequestQuery>) => {
    const { data } = await TaskLibraryApi.getCategories(param)
    agentCategoryItems.value = data?.results || []
    agentCategoryTotal.value = data.count
  }

  const getAgentsItems = async (param?: Partial<IListAgentRequestQuery>) => {
    const { data } = await TaskLibraryApi.getAgents({ limit: 10, ...param })
    agentsItems.value = data?.results || []
    countAgents.value = data.count
    agentId.value = data?.results.length ? data?.results[0].agent_id : undefined
  }

  const getAgentsByCategory = async (
    categoryCode: string,
    param?: Partial<IListAgentRequestQuery>,
    isAppend?: boolean,
  ) => {
    const limit = param?.limit || 10

    const { data } = await TaskLibraryApi.getAgents({
      limit,
      ...param,
      category: [categoryCode],
    })

    const existing = agentsByCategory.value[categoryCode]
    const newItems = data?.results || []
    const total = data?.count || 0
    const page = param?.page || 1

    agentsByCategory.value = {
      ...agentsByCategory.value,
      [categoryCode]: {
        items: isAppend && existing ? [...existing.items, ...newItems] : newItems,
        total,
        next: data.next,
        limit,
        page,
      },
    }

    return {
      items: newItems,
      total,
      hasMore: data.next !== undefined || data.next !== null,
    }
  }

  const clearAgentsByCategory = (categoryCode?: string) => {
    if (categoryCode) {
      delete agentsByCategory.value[categoryCode]
    } else {
      agentsByCategory.value = {}
    }
  }

  const agent = computed(() => {
    let current = (agentsItems.value || []).find((a: IAgent) => a.agent_id === agentId.value)
    if (!current && agentsItems.value.length > 0) {
      current = agentsItems.value[0]
    }
    return current
  })

  watch(
    () => router.currentRoute.value.query.agent_id,
    () => {
      setAgentId(router.currentRoute.value.query.agent_id?.toString())
    },
  )

  const setAgentId = (id?: string) => {
    agentId.value = id
  }

  return {
    agentId,
    agentCategoryItems,
    agentCategoryTotal,
    agentsItems,
    agentsByCategory,
    countAgents,
    agent,
    setAgentId,
    getCategoryItems,
    getAgentsItems,
    getAgentsByCategory,
    clearAgentsByCategory,
  }
})
